import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const VOTE_SITES = [
  {
    id: "topminecraftservers",
    name: "Top MC Servers",
    url: "https://topminecraftservers.org/vote/34446",
    domain: "topminecraftservers.org",
  },
  {
    id: "minecraft-server-list",
    name: "Minecraft Server List",
    url: "https://minecraft-server-list.com/server/498334/vote/",
    domain: "minecraft-server-list.com",
  },
  {
    id: "minecraft-mp",
    name: "Minecraft-MP",
    url: "https://minecraft-mp.com/server/322241/vote/",
    domain: "minecraft-mp.com",
  },
  {
    id: "curseforge",
    name: "CurseForge",
    url: "https://www.curseforge.com/servers/minecraft/game/mcverse-no-grief-survival/vote",
    domain: "curseforge.com",
  },
  {
    id: "planetminecraft",
    name: "Planet Minecraft",
    url: "https://www.planetminecraft.com/server/mcverse-city/vote/",
    domain: "planetminecraft.com",
  },
  {
    id: "minecraftservers",
    name: "MinecraftServers.org",
    url: "https://minecraftservers.org/vote/676938",
    domain: "minecraftservers.org",
  },
  {
    id: "findmcserver",
    name: "FindMCServer",
    url: "https://findmcserver.com/server/mcversecity?vote=true",
    domain: "findmcserver.com",
  },
  {
    id: "16bitmc",
    name: "16BitMC",
    url: "https://16bitmc.net/vote/219",
    domain: "16bitmc.net",
  },
];

const STORAGE_KEY = "mcverse-votes";
const ONE_HOUR_MS = 24 * 60 * 60 * 1000;

function loadVotedSites() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(stored).filter(([, ts]) => now - ts < ONE_HOUR_MS),
    );
  } catch {
    return {};
  }
}

function getFaviconUrl(domain) {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
}

export default function Vote() {
  const [votedSites, setVotedSites] = useState({});

  useEffect(() => {
    setVotedSites(loadVotedSites());

    // Refresh every minute to expire old votes
    const interval = setInterval(() => {
      setVotedSites(loadVotedSites());
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleVote = useCallback((site) => {
    window.open(site.url, "_blank", "noopener,noreferrer");

    setVotedSites((prev) => {
      const updated = { ...prev, [site.id]: Date.now() };
      try {
        // Merge with all stored votes (including non-expired ones not in state)
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ ...stored, [site.id]: Date.now() }),
        );
      } catch {
        // ignore storage errors
      }
      return updated;
    });
  }, []);

  const votedCount = VOTE_SITES.filter((s) => votedSites[s.id]).length;
  const progressPct = (votedCount / VOTE_SITES.length) * 100;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <HowToVoteIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          Vote for MCVerse
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Voting helps new players discover our server. <br />
          Each site lets you vote once every 24 hours.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Earn MCV currency and a vote point per vote.
        </Typography>

        {/* Progress */}
        <Box sx={{ maxWidth: 400, mx: "auto" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Links clicked (Resets after 24 hours)
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {votedCount} / {VOTE_SITES.length}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{ height: 8, borderRadius: 4 }}
            color={votedCount === VOTE_SITES.length ? "success" : "primary"}
          />
          {votedCount === VOTE_SITES.length && (
            <Typography
              variant="body2"
              color="success.main"
              fontWeight={600}
              sx={{ mt: 1 }}
            >
              All votes cast! Thank you for supporting MCVerse!
            </Typography>
          )}
        </Box>
      </Box>

      {/* Vote Cards */}
      <Grid container spacing={2}>
        {VOTE_SITES.map((site) => {
          const voted = Boolean(votedSites[site.id]);
          return (
            <Grid item xs={12} sm={6} key={site.id}>
              <Tooltip
                title={
                  voted ? "Voted in the last hour" : `Vote on ${site.name}`
                }
                placement="top"
              >
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: voted ? "success.main" : "divider",
                    bgcolor: voted ? "#f0fdf4" : "background.paper",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: voted ? "success.dark" : "primary.main",
                      boxShadow: 2,
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleVote(site)}
                    sx={{ p: 0 }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        py: 2,
                        "&:last-child": { pb: 2 },
                      }}
                    >
                      {/* Favicon */}
                      <Box
                        component="img"
                        src={getFaviconUrl(site.domain)}
                        alt={`${site.name} favicon`}
                        sx={{
                          width: 32,
                          height: 32,
                          flexShrink: 0,
                          borderRadius: 0.5,
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />

                      {/* Site name */}
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ flexGrow: 1, lineHeight: 1.3 }}
                      >
                        {site.name}
                      </Typography>

                      {/* Status */}
                      {voted ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Voted"
                          color="success"
                          size="small"
                          sx={{ flexShrink: 0 }}
                        />
                      ) : (
                        <Chip
                          icon={<OpenInNewIcon />}
                          label="Vote"
                          color="primary"
                          size="small"
                          variant="outlined"
                          sx={{ flexShrink: 0 }}
                        />
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ display: "block", textAlign: "center", mt: 4 }}
      >
        Vote tracking resets after 24 hours. <br />
        Actual site cooldowns are 24 hours, but varies.
        <br />
        <br />
        This tool only tracks CLICKING the button. <br />
        It does not yet know if your vote counted. (yet)
      </Typography>
    </Container>
  );
}
